// src/pages/AffectationsControleursList.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost/securecontrol-backend";

const COLORS = {
  primary: "#0B3C5D",
  secondary: "#1E88C8",
  accent: "#4FC3F7",
  darkText: "#2E2E2E",
  lightBg: "#F5F7FA",
  white: "#FFFFFF",
  danger: "#f44336",
  success: "#4caf50",
  warning: "#ff9800",
  info: "#2196f3",
  successLight: "#e8f5e9",
  dangerLight: "#ffebee",
  warningLight: "#fff3e0",
  infoLight: "#e3f2fd",
};

const formatDate = (dateString) => {
  if (
    !dateString ||
    dateString === "0000-00-00" ||
    dateString === "0000-00-00 00:00:00"
  )
    return "-";
  try {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

const AffectationsControleursList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const siteIdParam = queryParams.get("site_id");
  const controleurIdParam = queryParams.get("controleur_id");

  const [affectations, setAffectations] = useState([]);
  const [statistiques, setStatistiques] = useState({
    total_sites: 0,
    total_controleurs: 0,
    total_affectations: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [filters, setFilters] = useState({
    id_site: siteIdParam || "",
    id_user: controleurIdParam || "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [hoveredDelete, setHoveredDelete] = useState(null);
  const [filterSites, setFilterSites] = useState([]);
  const [filterControleurs, setFilterControleurs] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUserInfo({
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0,
          id_user: userData.id_user || 0,
        });
      } catch {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const fetchFilterData = useCallback(async () => {
    if (!userInfo) return;
    const token = localStorage.getItem("access_token");
    if (!token) return;
    setLoadingFilters(true);
    try {
      // Sites
      let sitesData = [];
      for (const endpoint of [
        `${API_BASE_URL}/sites/list.php?limit=1000`,
        `${API_BASE_URL}/sites.php?action=list&limit=1000`,
        `${API_BASE_URL}/sites.php`,
      ]) {
        try {
          const r = await axios.get(endpoint, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (r.data?.success) {
            sitesData = r.data.data || r.data.sites || [];
            break;
          }
        } catch {}
      }
      setFilterSites(sitesData);

      // Contrôleurs
      let controleursData = [];
      for (const endpoint of [
        `${API_BASE_URL}/users/list.php?profile=2&limit=1000`,
        `${API_BASE_URL}/users.php?profile=2`,
      ]) {
        try {
          const r = await axios.get(endpoint, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (r.data?.success) {
            controleursData = r.data.data || r.data.users || [];
            break;
          }
        } catch {}
      }
      setFilterControleurs(controleursData);
    } catch (err) {
      console.error("Erreur chargement filtres:", err);
    } finally {
      setLoadingFilters(false);
    }
  }, [userInfo]);

  const fetchAffectations = useCallback(async () => {
    if (!userInfo) return;
    if (String(userInfo.profile).trim() === "1") {
      setError(
        "Permission refusée. L'administrateur système ne peut pas accéder aux affectations contrôleurs.",
      );
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    const token = localStorage.getItem("access_token");
    if (!token) {
      setError("Session expirée");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    try {
      let url = `${API_BASE_URL}/affectations/list-controleurs.php`;
      const params = [];
      if (filters.id_site) params.push(`id_site=${filters.id_site}`);
      if (filters.id_user) params.push(`id_user=${filters.id_user}`);
      if (params.length > 0) url += `?${params.join("&")}`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        timeout: 10000,
      });

      if (response.data?.success) {
        const data = response.data.data || [];
        setAffectations(data);
        if (response.data.statistiques) {
          setStatistiques(response.data.statistiques);
        } else {
          setStatistiques({
            total_sites: [...new Set(data.map((a) => a.id_site))].length,
            total_controleurs: [...new Set(data.map((a) => a.id_user))].length,
            total_affectations: data.length,
          });
        }
      } else {
        setError(
          response.data?.message || "Erreur de chargement des affectations",
        );
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Session expirée.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setError("Accès interdit.");
      } else if (err.code === "ECONNABORTED" || !err.response) {
        setError("Serveur indisponible.");
      } else {
        setError(
          `Erreur ${err.response?.status}: ${err.response?.data?.message || "Erreur technique"}`,
        );
      }
    } finally {
      setLoading(false);
    }
  }, [userInfo, navigate, filters]);

  useEffect(() => {
    if (userInfo) {
      fetchFilterData();
      fetchAffectations();
    }
  }, [userInfo, fetchFilterData, fetchAffectations]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setShowFilters(false);
    fetchAffectations();
  };
  const resetFilters = () => {
    setFilters({ id_site: "", id_user: "" });
    setShowFilters(false);
    fetchAffectations();
  };

  const handleDeleteAffectation = () => {
    alert(
      "Fonctionnalité de suppression non implémentée pour les contrôleurs.",
    );
  };

  const handleViewDetails = (affectation) => {
    navigate(`/affectations-controleurs/detail/${affectation.id_affectation}`);
  };

  const isGestionnaire = userInfo && String(userInfo.profile).trim() === "3";

  const formatControleurName = (controleur) => {
    if (!controleur) return "Inconnu";
    if (controleur.prenom && controleur.nom)
      return `${controleur.prenom} ${controleur.nom}`;
    if (controleur.nom_user) return controleur.nom_user;
    return `Contrôleur ${controleur.id_user}`;
  };

  const formatSiteName = (site) => {
    if (!site) return "Inconnu";
    return site.nom || site.nom_site || `Site ${site.id_site}`;
  };

  const getSiteAdresse = (site) =>
    site?.adresse || site?.addresse_site || "Pas d'adresse";

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
            Affectations des contrôleurs
          </h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>
                  Tableau de bord
                </Link>
              </li>
              <li
                className="breadcrumb-item active"
                style={{ color: COLORS.secondary }}
              >
                Chargement...
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div
            className="card"
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              border: "none",
            }}
          >
            <div className="card-body text-center py-5">
              <div
                className="spinner-border"
                role="status"
                style={{ color: COLORS.primary }}
              >
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-2" style={{ color: COLORS.darkText }}>
                Chargement des affectations...
              </p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (error && !loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
            Affectations des contrôleurs
          </h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.primary }}>
                  Tableau de bord
                </Link>
              </li>
              <li
                className="breadcrumb-item active"
                style={{ color: COLORS.danger }}
              >
                Erreur
              </li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div
            className="card"
            style={{
              borderRadius: "12px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              border: "none",
            }}
          >
            <div className="card-body">
              <div
                className="alert alert-danger alert-dismissible fade show"
                style={{
                  backgroundColor: "#ffebee",
                  borderColor: COLORS.danger,
                  color: "#c62828",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${COLORS.danger}`,
                }}
              >
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{error}</div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError("")}
                  ></button>
                </div>
              </div>
              <div className="text-center mt-3">
                <button
                  className="btn fw-semibold"
                  onClick={() => navigate("/dashboard")}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: "transparent",
                    borderRadius: "8px",
                    padding: "10px 24px",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "transparent";
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>Retour au tableau de
                  bord
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: "600" }}>
          Affectations des contrôleurs
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>
                Tableau de bord
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link
                to="/affectations-controleurs"
                style={{ color: COLORS.secondary }}
              >
                Affectations Contrôleurs
              </Link>
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div
          className="card"
          style={{
            borderRadius: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "none",
          }}
        >
          <div className="card-body">
            {/* ── En-tête ── */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div style={{ width: "100%" }}>
                <h5
                  className="card-title mb-1"
                  style={{
                    color: COLORS.primary,
                    borderBottom: `2px solid ${COLORS.lightBg}`,
                    paddingBottom: "16px",
                    width: "100%",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <div
                      className="rounded-circle d-inline-flex align-items-center justify-content-center me-3"
                      style={{
                        width: "36px",
                        height: "36px",
                        backgroundColor: "#E3F2FD",
                        color: COLORS.primary,
                      }}
                    >
                      <i className="bi bi-people"></i>
                    </div>
                    Liste des affectations
                    <span
                      className="badge ms-3"
                      style={{
                        backgroundColor: COLORS.secondary,
                        color: COLORS.white,
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "0.8rem",
                      }}
                    >
                      {affectations.length} trouvée(s)
                    </span>
                  </div>
                </h5>
                <p className="text-muted mb-0" style={{ fontSize: "0.9rem" }}>
                  <i className="bi bi-info-circle me-1"></i>
                  Gérez les affectations des contrôleurs aux sites
                  {loadingFilters && (
                    <small className="ms-2" style={{ color: COLORS.secondary }}>
                      Chargement des filtres...
                    </small>
                  )}
                </p>
              </div>

              <div className="d-flex gap-2 ms-3" style={{ flexShrink: 0 }}>
                {/* Bouton Filtres */}
                <button
                  className="btn fw-semibold"
                  onClick={() => setShowFilters(!showFilters)}
                  style={{
                    color: COLORS.primary,
                    borderColor: COLORS.primary,
                    backgroundColor: "transparent",
                    borderRadius: "8px",
                    padding: "10px 20px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#E3F2FD";
                    e.currentTarget.style.borderColor = COLORS.secondary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.borderColor = COLORS.primary;
                  }}
                >
                  <i className="bi bi-funnel me-2"></i>Filtres
                  {(filters.id_site || filters.id_user) && (
                    <span
                      className="badge fw-semibold ms-1"
                      style={{
                        backgroundColor: COLORS.danger,
                        color: "white",
                        fontSize: "0.7rem",
                      }}
                    >
                      !
                    </span>
                  )}
                </button>

                {isGestionnaire && (
                  <button
                    className="btn fw-semibold"
                    onClick={() => navigate("/affectations-controleurs/create")}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: "8px",
                      padding: "10px 24px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.secondary;
                      e.currentTarget.style.borderColor = COLORS.secondary;
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.primary;
                      e.currentTarget.style.borderColor = COLORS.primary;
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <i className="bi bi-plus-lg me-2"></i>Nouvelle affectation
                  </button>
                )}
              </div>
            </div>

            {/* ── Panneau filtres ── */}
            {showFilters && (
              <div
                className="card mb-4"
                style={{
                  backgroundColor: "#E3F2FD",
                  border: "none",
                  borderRadius: "8px",
                }}
              >
                <div className="card-body py-3">
                  <h6
                    className="card-title mb-3"
                    style={{
                      color: COLORS.primary,
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div
                      className="rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                      style={{
                        width: "28px",
                        height: "28px",
                        backgroundColor: COLORS.white,
                        color: COLORS.primary,
                      }}
                    >
                      <i className="bi bi-funnel"></i>
                    </div>
                    Filtres de recherche
                    {loadingFilters && (
                      <span
                        className="ms-2 spinner-border spinner-border-sm"
                        style={{
                          color: COLORS.primary,
                          width: "1rem",
                          height: "1rem",
                        }}
                      ></span>
                    )}
                  </h6>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="input-group">
                        <span
                          className="input-group-text"
                          style={{
                            backgroundColor: COLORS.white,
                            borderColor: "#e0e0e0",
                            color: COLORS.secondary,
                          }}
                        >
                          <i className="bi bi-building"></i>
                        </span>
                        <select
                          className="form-select"
                          name="id_site"
                          value={filters.id_site}
                          onChange={handleFilterChange}
                          style={{
                            borderColor: "#e0e0e0",
                            color: COLORS.darkText,
                          }}
                          disabled={loadingFilters}
                        >
                          <option value="">Tous les sites</option>
                          {filterSites.length > 0 ? (
                            filterSites.map((s) => (
                              <option
                                key={s.id_site || s.id}
                                value={s.id_site || s.id}
                              >
                                {s.nom_site || s.nom}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              Aucun site disponible
                            </option>
                          )}
                        </select>
                      </div>
                      <div
                        className="form-text"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {filterSites.length} site(s) disponible(s)
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="input-group">
                        <span
                          className="input-group-text"
                          style={{
                            backgroundColor: COLORS.white,
                            borderColor: "#e0e0e0",
                            color: COLORS.secondary,
                          }}
                        >
                          <i className="bi bi-person"></i>
                        </span>
                        <select
                          className="form-select"
                          name="id_user"
                          value={filters.id_user}
                          onChange={handleFilterChange}
                          style={{
                            borderColor: "#e0e0e0",
                            color: COLORS.darkText,
                          }}
                          disabled={loadingFilters}
                        >
                          <option value="">Tous les contrôleurs</option>
                          {filterControleurs.length > 0 ? (
                            filterControleurs.map((c) => (
                              <option
                                key={c.id_user || c.id}
                                value={c.id_user || c.id}
                              >
                                {c.prenom && c.nom
                                  ? `${c.prenom} ${c.nom}`
                                  : c.nom_user ||
                                    `Contrôleur ${c.id_user || c.id}`}
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              Aucun contrôleur disponible
                            </option>
                          )}
                        </select>
                      </div>
                      <div
                        className="form-text"
                        style={{ fontSize: "0.75rem" }}
                      >
                        {filterControleurs.length} contrôleur(s) disponible(s)
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button
                      className="btn fw-semibold"
                      onClick={resetFilters}
                      disabled={loadingFilters}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: "transparent",
                        borderRadius: "8px",
                        padding: "8px 20px",
                      }}
                      onMouseEnter={(e) => {
                        if (!loadingFilters) {
                          e.currentTarget.style.backgroundColor = "#E3F2FD";
                          e.currentTarget.style.borderColor = COLORS.secondary;
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.borderColor = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-arrow-clockwise me-1"></i>
                      Réinitialiser
                    </button>
                    <button
                      className="btn fw-semibold"
                      onClick={applyFilters}
                      disabled={loadingFilters}
                      style={{
                        color: COLORS.white,
                        backgroundColor: COLORS.primary,
                        borderColor: COLORS.primary,
                        borderRadius: "8px",
                        padding: "8px 20px",
                      }}
                      onMouseEnter={(e) => {
                        if (!loadingFilters) {
                          e.currentTarget.style.backgroundColor =
                            COLORS.secondary;
                          e.currentTarget.style.borderColor = COLORS.secondary;
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = COLORS.primary;
                        e.currentTarget.style.borderColor = COLORS.primary;
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <i className="bi bi-check-lg me-1"></i>Appliquer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Cards statistiques (inchangées) ── */}
            <div className="row mb-4">
              {/* Sites affectés */}
              <div className="col-md-4 mb-3">
                <div
                  className="card"
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    border: "none",
                    borderLeft: `4px solid ${COLORS.primary}`,
                  }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "50px",
                          height: "50px",
                          backgroundColor: "#E3F2FD",
                          color: COLORS.primary,
                        }}
                      >
                        <i className="bi bi-building fs-4"></i>
                      </div>
                      <div>
                        <div
                          className="small"
                          style={{
                            color: COLORS.primary,
                            fontWeight: "600",
                            textTransform: "uppercase",
                          }}
                        >
                          Sites affectés
                        </div>
                        <h4
                          className="mt-1 mb-0"
                          style={{ color: COLORS.darkText, fontWeight: "700" }}
                        >
                          {statistiques.total_sites}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contrôleurs affectés */}
              <div className="col-md-4 mb-3">
                <div
                  className="card"
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    border: "none",
                    borderLeft: `4px solid ${COLORS.success}`,
                  }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "50px",
                          height: "50px",
                          backgroundColor: "#e8f5e9",
                          color: COLORS.success,
                        }}
                      >
                        <i className="bi bi-person fs-4"></i>
                      </div>
                      <div>
                        <div
                          className="small"
                          style={{
                            color: COLORS.success,
                            fontWeight: "600",
                            textTransform: "uppercase",
                          }}
                        >
                          Contrôleurs affectés
                        </div>
                        <h4
                          className="mt-1 mb-0"
                          style={{ color: COLORS.darkText, fontWeight: "700" }}
                        >
                          {statistiques.total_controleurs}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Affectations totales */}
              <div className="col-md-4 mb-3">
                <div
                  className="card"
                  style={{
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    border: "none",
                    borderLeft: `4px solid ${COLORS.warning}`,
                  }}
                >
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "50px",
                          height: "50px",
                          backgroundColor: "#fff3e0",
                          color: COLORS.warning,
                        }}
                      >
                        <i className="bi bi-link fs-4"></i>
                      </div>
                      <div>
                        <div
                          className="small"
                          style={{
                            color: COLORS.warning,
                            fontWeight: "600",
                            textTransform: "uppercase",
                          }}
                        >
                          Affectations totales
                        </div>
                        <h4
                          className="mt-1 mb-0"
                          style={{ color: COLORS.darkText, fontWeight: "700" }}
                        >
                          {statistiques.total_affectations}
                        </h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Tableau ── */}
            {affectations.length === 0 ? (
              <div className="text-center py-5">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-4"
                  style={{
                    width: "80px",
                    height: "80px",
                    backgroundColor: "#F5F7FA",
                    color: "#B0BEC5",
                  }}
                >
                  <i className="bi bi-people fs-1"></i>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>
                  Aucune affectation trouvée
                </h6>
                <p
                  className="text-muted"
                  style={{ maxWidth: "500px", margin: "0 auto" }}
                >
                  {filters.id_site || filters.id_user
                    ? "Aucune affectation ne correspond à vos critères de recherche."
                    : "Aucune affectation contrôleur-site n'a été créée pour le moment."}
                </p>
                {isGestionnaire && (
                  <button
                    className="btn fw-semibold mt-3"
                    onClick={() => navigate("/affectations-controleurs/create")}
                    style={{
                      color: COLORS.white,
                      backgroundColor: COLORS.primary,
                      borderColor: COLORS.primary,
                      borderRadius: "8px",
                      padding: "10px 24px",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.secondary;
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = COLORS.primary;
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <i className="bi bi-plus-lg me-1"></i>Créer la première
                    affectation
                  </button>
                )}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr
                      style={{
                        backgroundColor: COLORS.lightBg,
                        borderBottom: `2px solid ${COLORS.accent}`,
                      }}
                    >
                      {[
                        "Contrôleur",
                        "Nombre de sites affectés",
                        "Actions",
                      ].map((h, i) => (
                        <th
                          key={h}
                          style={{
                            color: COLORS.primary,
                            fontWeight: "600",
                            padding: "16px",
                            textAlign: i === 2 ? "center" : "left",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Dédupliquer : une ligne par contrôleur unique */}
                    {Array.from(
                      new Map(affectations.map((a) => [a.id_user, a])).values(),
                    ).map((affectation) => {
                      const controleurName = formatControleurName(
                        affectation.controleur,
                      );
                      const sitesCount = affectations.filter(
                        (a) => a.id_user === affectation.id_user,
                      ).length;
                      const rowKey = `ctrl-${affectation.id_user}`;

                      return (
                        <tr
                          key={rowKey}
                          style={{
                            borderBottom: `1px solid ${COLORS.lightBg}`,
                          }}
                        >
                          {/* ── Contrôleur ── */}
                          <td style={{ padding: "16px" }}>
                            <div className="d-flex align-items-center">
                              <div
                                className="text-white rounded-circle d-flex align-items-center justify-content-center fw-semibold me-3"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  fontSize: "14px",
                                  backgroundColor: COLORS.primary,
                                  flexShrink: 0,
                                  boxShadow: "0 2px 4px rgba(11,60,93,0.1)",
                                }}
                              >
                                {controleurName.charAt(0).toUpperCase()}
                              </div>
                              <div
                                style={{
                                  color: COLORS.darkText,
                                  fontWeight: "600",
                                  fontSize: "0.95rem",
                                }}
                              >
                                {controleurName}
                              </div>
                            </div>
                          </td>

                          {/* ── Nombre de sites ── */}
                          <td style={{ padding: "16px" }}>
                            <span
                              className="badge fw-semibold"
                              style={{
                                backgroundColor: COLORS.infoLight,
                                color: COLORS.info,
                                padding: "8px 12px",
                                borderRadius: "6px",
                                fontSize: "0.9rem",
                              }}
                            >
                              <i className="bi bi-building me-1"></i>
                              {sitesCount}
                            </span>
                          </td>

                          {/* ── Actions ── */}
                          <td style={{ padding: "16px", textAlign: "center" }}>
                            <button
                              className="btn btn-sm fw-semibold"
                              onClick={() => handleViewDetails(affectation)}
                              title="Voir les détails"
                              style={{
                                color: COLORS.primary,
                                borderColor: COLORS.primary,
                                backgroundColor: "transparent",
                                borderRadius: "6px",
                                padding: "6px 14px",
                                minWidth: "44px",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  COLORS.primary;
                                e.currentTarget.style.color = "white";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "transparent";
                                e.currentTarget.style.color = COLORS.primary;
                              }}
                            >
                              <i className="bi bi-eye"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .table-hover tbody tr:hover { background-color: ${COLORS.lightBg}; transition: background-color 0.2s ease; }
        .table th { text-transform: uppercase; letter-spacing: 0.5px; font-size: 0.85rem; }
        .table td { vertical-align: middle; }
        .btn:focus, .form-control:focus, .form-select:focus {
          outline: none; box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        .btn, .badge { transition: all 0.2s ease-in-out; }
        .btn:disabled { cursor: not-allowed; opacity: 0.6; }
        @media (max-width: 768px) {
          .card { margin: 0 -16px; border-radius: 0 !important; }
          .pagetitle h1 { font-size: 1.5rem; }
          .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .table th, .table td { padding: 12px 8px !important; }
          .col-md-4 { margin-bottom: 16px; }
        }
      `}</style>
    </Layout>
  );
};

export default AffectationsControleursList;
